import React, { useEffect, useState, startTransition } from 'react';
import { Routes, Route } from 'react-router-dom';
import { useAppState } from 'src/state-management/state-provider';
import { isAuthenticated, clearAuthTokens, getAccessTokenFromStorage } from 'src/clientService';
import { jwtDecode } from 'jwt-decode';
import { AppSchemasAuthUserResponse } from 'src/api/Api';
import FullPageLoader from 'src/view/components/FullPageLoader';
import FullscreenWrapper from 'src/view/components/FullscreenWrapper';
import AuthPage from 'src/view/AuthPage';
import PasswordResetPage from 'src/view/PasswordReset';
import AuthenticatedRoutes from './AuthenticatedRoutes';

const AppRouter: React.FC = () => {
  const { dispatch, state } = useAppState();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const verifyAuth = () => {
      if (isAuthenticated()) {
        try {
          const accessToken = getAccessTokenFromStorage();
          if (accessToken) {
            const decodedToken: any = jwtDecode(accessToken);
            const user: AppSchemasAuthUserResponse = {
              user_id: decodedToken.user_id,
              email: decodedToken.sub,
              first_name: decodedToken.first_name,
              last_name: decodedToken.last_name,
              role: decodedToken.role,
              status: decodedToken.status,
              is_email_verified: decodedToken.is_email_verified
            };
            if (isMounted) {
              startTransition(() => {
                dispatch({ type: 'SET_USER_DETAILS', payload: user });
                setLoading(false);
              });
            }
          } else {
            if (isMounted) {
              startTransition(() => {
                setLoading(false);
              });
            }
          }
        } catch (error) {
          console.error("Authentication failed or token invalid:", error);
          clearAuthTokens();
          if (isMounted) {
            startTransition(() => {
              dispatch({ type: 'CLEAR_USER_DETAILS' });
              setLoading(false);
            });
          }
        }
      } else {
        if (isMounted) {
          startTransition(() => {
            dispatch({ type: 'CLEAR_USER_DETAILS' });
            setLoading(false);
          });
        }
      }
    };

    verifyAuth();

    return () => {
      isMounted = false;
    };
  }, [dispatch]);

  if (loading) {
    return <FullscreenWrapper><FullPageLoader message="Verifying authentication..." title="Hang in there..." /></FullscreenWrapper>;
  }

  // If not authenticated after loading, redirect to AuthPage
  if (!state.user) {
    return (
      <Routes>
        <Route path="/reset-password" element={<PasswordResetPage />} />
        <Route path="*" element={<AuthPage />} />
      </Routes>
    );
  }

  // Do NOT wrap AuthenticatedRoutes in <Routes>
  return <AuthenticatedRoutes />;
};

export default AppRouter;